import 'reactApp/swiper.scss';

import sha1 from 'js-sha1';
import { ReactComponent as EditIcon } from 'mrg-icons/src/mailru/actions/compose.svg';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { CSSTransition } from 'react-transition-group';
import { openEditor } from 'reactApp/appHelpers/appHelpers';
import { IS_PUBLIC_ALBUM } from 'reactApp/appHelpers/configHelpers';
import { getFacesFileId } from 'reactApp/modules/faces/faces.helpers';
import { getFacesIdxOnFile } from 'reactApp/modules/faces/faces.selectors';
import { getEditorsFor } from 'reactApp/modules/editor/editor.selectors';
import { getMyOfficeTouchFeature } from 'reactApp/modules/features/features.selectors';
import { getWeblinkFromPublicId, isDocument } from 'reactApp/modules/file/utils';
import { getCurrentRouteId, getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { changeStorageHistory, getStorage } from 'reactApp/modules/storage/storage.helpers';
import { getStorageCurrentFolder } from 'reactApp/modules/storage/storage.selectors';
import { selectItemIndexInArray } from 'reactApp/modules/viewer/viewer.module';
import { ViewerSelectors } from 'reactApp/modules/viewer/viewer.selectors';
import { RootState } from 'reactApp/store';
import { MobileViewerItem } from 'reactApp/ui/Mobile/MobileViewer/MobileViewerItem/MobileViewerItem';
import { ViewerToolbar } from 'reactApp/ui/Mobile/MobileViewer/ViewerToolbar/ViewerToolbar';
import { renderMobileOptionsModal } from 'reactApp/ui/Mobile/OptionsModal/OptionsModal.helpers';
import { sendViewerDwh } from 'reactApp/ui/ReactViewer/ReactViewer.helpers';
import { ViewerButton } from 'reactApp/ui/ReactViewer/ViewerButton/ViewerButton';
import { SwipingDownComponent } from 'reactApp/ui/SwipingDownComponent/SwipingDownComponent';
import { createGaSender } from 'reactApp/utils/ga';
import { noop } from 'reactApp/utils/helpers';
import { scrollLock, scrollUnlock } from 'reactApp/utils/scrollLock';
import SwiperCore, { Virtual, Zoom } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import styles from './MobileViewer.css';

SwiperCore.use([Zoom, Virtual]);

const GA_CATEGORY = 'mobile-viewer';
const sendViewerGa = createGaSender(GA_CATEGORY);

const SWIPER_SPEED = 350;

interface Props {
    onClose: () => void;
}

const virtualOptions = {
    addSlidesBefore: 3,
    addSlidesAfter: 3,
};

const animationClasses = {
    exit: styles.toolbar_exit,
    exitActive: styles.toolbar_exit_active,
    enter: styles.toolbar_enter,
    enterActive: styles.toolbar_enter_active,
};

export const MobileViewer = memo<Props>(
    // eslint-disable-next-line max-lines-per-function
    ({ onClose = noop }) => {
        const dispatch = useDispatch();
        const store = useStore();

        const itemId = useSelector(ViewerSelectors.getViewerItemId);
        const itemsIdsList = useSelector(ViewerSelectors.getViewerItemIds) || [];
        const itemIndex = useSelector(ViewerSelectors.getViewerItemIndexInArray) || 0;
        const totalCount = useSelector(ViewerSelectors.getViewerItemCount);
        const currentItem = useSelector(ViewerSelectors.getViewerItem);
        const myOfficeTouchFeature = useSelector(getMyOfficeTouchFeature);
        const isEditable = !!useSelector((state: RootState) => getEditorsFor(state, currentItem))?.length;

        const hasMyOfficeFeature = myOfficeTouchFeature && isDocument(currentItem);
        const showMyOfficeEditButton =
            isEditable && hasMyOfficeFeature && myOfficeTouchFeature.editablePages.includes(getCurrentStorage(store.getState()));

        const [swiper, setSwiper] = useState<SwiperCore | null>(null);
        const [showCustomItem, setShowCustomItem] = useState(false);
        const [showToolbar, setShowToolbar] = useState(!showCustomItem);
        const [isZoom, setIsZoom] = useState(false);
        const [swipeCount, setSwiperCount] = useState<number>(0);
        const timerId = useRef<number | undefined>(undefined);

        /**
         * После обратноно маунта тулбара после анмаунта, руками показываем тулбар,
         * иначе он отобразиться только если потапать на экран
         */
        useEffect(() => {
            if (!showCustomItem) {
                setShowToolbar(true);
            }
        }, [showCustomItem]);

        const rootRef = useRef<null | any>(null);

        const ext = (!currentItem?.isFolder && currentItem?.ext?.toLowerCase()) || '';
        const nameWithoutExt = (!currentItem?.isFolder && currentItem?.nameWithoutExt) || '';

        const fileId = getFacesFileId(itemId || '');
        const facesIdx = useSelector((state: RootState) => getFacesIdxOnFile(state, fileId));

        const sendDwh = useCallback(
            ({ action, source, ...rest }) => {
                const storage = getCurrentStorage(store.getState());
                const { isPublic } = getStorage(storage);
                const touchSource = IS_PUBLIC_ALBUM ? 'album' : source || storage;

                sendViewerDwh({
                    action,
                    id_media: sha1(currentItem?.id || ''),
                    type_content: currentItem?.kind,
                    extension: ext,
                    source: `touch-${touchSource}`,
                    is_stories: false,
                    id_public: isPublic && currentItem?.id ? getWeblinkFromPublicId(currentItem?.id) : 'None',
                    ...rest,
                });
            },
            [currentItem, ext]
        );

        const closeViewer = useCallback(() => {
            const state = store.getState();
            const storage = getCurrentStorage(state);
            const { isGallery } = getStorage(storage);
            const folder = getStorageCurrentFolder(state, storage);

            onClose();
            if (!isGallery) {
                changeStorageHistory({ id: folder?.id, storage });
            }
        }, [onClose]);

        useEffect(() => {
            sendViewerGa('open', '', { count: itemsIdsList.length });

            sendDwh({ action: 'open' });

            const el = rootRef?.current;

            scrollLock(el, { reserveScrollBarGap: false, allowTouchMove: true });

            return () => scrollUnlock(el);
        }, []);

        /**
         *  Когда открыли вьювер на 500+ фотографии и перезагрузили страницу.
         *  Скролимся до нужного элемента.
         */
        useEffect(() => {
            if (itemsIdsList.length === 0) {
                closeViewer();
                return;
            }

            if (swiper?.activeIndex !== itemIndex) {
                swiper?.virtual?.update(true);
                swiper?.slideTo(itemIndex, SWIPER_SPEED);
            }
        }, [itemsIdsList.length, closeViewer, swiper]);

        // менять слайд, если нажали браузерную кнопку назад
        useEffect(() => {
            const state = store.getState();
            const storage = getCurrentStorage(state);
            const { isHome, isStock, isGallery } = getStorage(storage);
            const currentRoute = getCurrentRouteId(state);
            /**
             * TODO: убрать после починки роутера. на хомяки и стоках в currentRoute приходит не полный путь:
             * Вместо /папка/файл.jpg приходить только /папка.
             * Из-за этого вся логика ниже работает некорректно.
             */
            if (isHome || isStock) {
                return;
            }

            // У элементов галерее нет своего пути.
            if (isGallery) {
                return;
            }

            if (itemId === currentRoute) {
                return;
            }

            const routeIndex = itemsIdsList.indexOf(currentRoute);

            if (routeIndex !== -1 && swiper) {
                swiper.slideTo(routeIndex, SWIPER_SPEED);
            } else {
                closeViewer();
            }
        }, [closeViewer]);

        useEffect(() => {
            if (currentItem && swiper) {
                if (currentItem.kind !== 'image') {
                    swiper.zoom.disable();
                } else {
                    swiper.zoom.enable();
                }
            }
        }, [currentItem]);

        const handleClose = useCallback(
            (label: string) => {
                sendViewerGa('close', label);
                closeViewer();

                sendDwh({ action: 'close' });
            },
            [closeViewer, sendDwh]
        );

        const handleSlideChange = useCallback(
            (swiper: SwiperCore) => {
                const id = itemsIdsList[swiper.activeIndex];

                // если открыть просмотрщик не на первой фотке swiperJs вызывает slideChange, в этом случае отправлять радар не надо
                if (swipeCount !== 0 || itemId !== id) {
                    sendViewerGa('swipe', '', { swipeCount: swipeCount + 1 });
                    setSwiperCount((prevCount) => prevCount + 1);

                    sendDwh({
                        action: 'skip-photo',
                        type_click:
                            // Событие точно есть, но SwiperCore, описывает не все типы
                            (swiper as SwiperCore & { swipeDirection: 'next' | 'prev' }).swipeDirection === 'next' ? 'next' : 'previous',
                    });
                }

                dispatch(selectItemIndexInArray({ itemIndex: swiper.activeIndex }));
            },
            [dispatch, itemId, sendDwh]
        );

        const handleSlideChangeEnd = useCallback(() => {
            setIsZoom(false);
        }, []);

        const handleZoom = useCallback((_: SwiperCore, scale: number): void => {
            sendViewerGa('zoom');
            setShowToolbar(false);

            if (scale === 1) {
                setIsZoom(false);
            } else {
                setIsZoom(true);
            }
        }, []);

        const toggleToolbar = useCallback(() => {
            if (!timerId.current) {
                const id = window.setTimeout(() => {
                    setShowToolbar((prevValue) => !prevValue);
                    timerId.current = undefined;
                }, 500);
                timerId.current = id;
            }
        }, [timerId, setShowToolbar]);

        const handleOnDoubleClick = useCallback(() => {
            window.clearTimeout(timerId.current);
            timerId.current = undefined;
        }, [timerId]);

        const onInitialSwiper = useCallback((swiper: SwiperCore) => {
            setSwiper(swiper);
        }, []);

        const closeOnSwipe = useCallback(() => {
            handleClose('swipe');
        }, [handleClose]);

        const openToolbarOptions = useCallback(() => {
            renderMobileOptionsModal({ id: itemId, onlyActions: true, sendAnalytics: sendDwh, skipRename: true });
        }, [itemId, sendDwh]);

        const isLockSwipingDown = useMemo(() => {
            return isZoom || showCustomItem || currentItem?.kind === 'pdf';
        }, [isZoom, showCustomItem, currentItem]);

        return (
            <div className={styles.root} ref={rootRef}>
                <SwipingDownComponent onClose={closeOnSwipe} lock={isLockSwipingDown}>
                    <div className={styles.content}>
                        {!showCustomItem && (
                            <CSSTransition in={showToolbar} timeout={SWIPER_SPEED} unmountOnExit exit enter classNames={animationClasses}>
                                <ViewerToolbar
                                    onClose={handleClose}
                                    ext={ext}
                                    nameWithoutExt={nameWithoutExt}
                                    onClickOption={openToolbarOptions}
                                    subTitle={`${itemIndex + 1} из ${totalCount}`}
                                    itemId={itemId}
                                    faces={facesIdx}
                                />
                            </CSSTransition>
                        )}
                        <div className={styles.swiperWrapper}>
                            <Swiper
                                onSwiper={onInitialSwiper}
                                initialSlide={itemIndex}
                                spaceBetween={20}
                                onSlideChange={handleSlideChange}
                                zoom
                                // Запрещаем переключение слайдов на документах через мой офис
                                allowSlideNext={!hasMyOfficeFeature}
                                allowSlidePrev={!hasMyOfficeFeature}
                                onZoomChange={handleZoom}
                                onDoubleClick={handleOnDoubleClick}
                                onClick={toggleToolbar}
                                simulateTouch={false}
                                onSlideChangeTransitionEnd={handleSlideChangeEnd}
                                slidesPerView={1}
                                virtual={virtualOptions}
                                resizeObserver={false}
                                speed={150}
                            >
                                {itemsIdsList.map((item, index) => {
                                    return (
                                        <SwiperSlide key={item} zoom virtualIndex={index}>
                                            <MobileViewerItem
                                                openToolbarOptions={openToolbarOptions}
                                                setShowCustomItem={setShowCustomItem}
                                                selected={itemId === item}
                                                itemId={item}
                                            />
                                        </SwiperSlide>
                                    );
                                })}
                            </Swiper>
                        </div>
                        {showMyOfficeEditButton && (
                            <div className={styles.controls}>
                                <ViewerButton
                                    title="Редактировать документ"
                                    onClick={() => openEditor(currentItem)}
                                    icon={<EditIcon />}
                                    qaName="edit"
                                />
                            </div>
                        )}
                    </div>
                </SwipingDownComponent>
            </div>
        );
    }
);

MobileViewer.displayName = 'MobileViewer';
